import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AliasField from 'metadata/AliasField';
import {schemaFieldsetNamePluralized} from 'schemas/schema-interface/util/schemaFieldsetName';
import {
  SchemaFieldSelector,
  SchemaFieldSelectorOptionForUpdate,
  SchemaFieldSelectorOptionToUpdate,
} from 'schemas/SchemaFieldSelectorConstants';
import RelatedSchemasTable from 'schemas/shared/RelatedSchemasTable';
import {
  ExistingSchemaFieldSelectorQuery,
  ExistingSchemaFieldSelectorQuery_schemaFieldSelector_SchemaFieldSelectorNode_relatedSchemasAndInterfaces_wu8eeC9lCXGk7Rxb8uPV2qsCBClgpToW2FRJswU2vco as ArchivableSchemaNode,
  ExistingSchemaFieldSelectorQuery_schemaFieldSelector_SchemaFieldSelectorNode_relatedSchemasAndInterfaces_sqflQbaABILaKe0vC8UMMeDG9jZhIUN6i3iGAr49mjE as SchemaNode,
  ExistingSchemaFieldSelectorQuery_schemaFieldSelector_SchemaFieldSelectorNode_relatedSchemasAndInterfaces_SchemaInterfaceNode as SchemaInterfaceNode,
} from 'settings/registry/__generated/ExistingSchemaFieldSelectorQuery';
import SchemaFieldSelectorOptionsSettings from 'settings/registry/SchemaFieldSelectorOptionsSettings';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {Tab, TabContainer} from 'ui-library/TabContainer';

export interface SchemaFieldSelectorSettingsComponentProps {
  isWritable: () => boolean;
  options: Array<SchemaFieldSelectorOptionForUpdate>;
  setOptions: (options: Array<SchemaFieldSelectorOptionForUpdate>) => void;
  selectedArchivePurposes: Array<string>;
  handleChangeSelectedArchivePurposes: (selectedArchivePurposes: Array<string>) => void;
  selector: SchemaFieldSelector;
  selectorId: string;
  updateSelector: (
    name: string | null,
    options?: Array<SchemaFieldSelectorOptionForUpdate>,
    selectorOptionToUpdate?: SchemaFieldSelectorOptionToUpdate
  ) => void;
  validateSelectorName: (initialName: string, newName: string) => boolean;
}

export interface EnhancedProps extends SchemaFieldSelectorSettingsComponentProps {
  // Empty array is there are no related schemas or interfaces, null if the data is loading
  relatedSchemasAndInterfaces: Array<SchemaNode | ArchivableSchemaNode | SchemaInterfaceNode> | null;
}

export enum ExistingSchemaFieldSelectorTab {
  DROPDOWN_SETTINGS = 'DROPDOWN_SETTINGS',
  SCHEMAS = 'SCHEMAS',
}

function DropdownSettings({
  isWritable,
  options,
  setOptions,
  selectedArchivePurposes,
  handleChangeSelectedArchivePurposes,
  selector,
  selectorId,
  updateSelector,
  validateSelectorName,
}: SchemaFieldSelectorSettingsComponentProps): JSX.Element {
  const renameSelector = (initialName: string, newName: string): void => {
    /**
     * AliasField returns 2 values (initial and new name) if it had an initial value, else 1 value
     * AliasField has an initial value if we are editing a selector, but not if we are creating a selector
     */
    const sanitizedNewName = newName.trim();
    if (initialName === sanitizedNewName) {
      return;
    }
    const name = sanitizedNewName || initialName;
    updateSelector(name);
  };

  return (
    <div>
      <div className="settingsPageContainer-section u-spacing-top">
        <div className="metadata-header">Name</div>
        <div>
          <AliasField
            className="selectorSettings-field"
            complete={(newAlias: string) => {
              renameSelector(selector.name, newAlias);
            }}
            initialAlias={selector.name}
            isWritable={isWritable()}
            key="selector name"
            placeholder="Enter dropdown name"
            validate={validateSelectorName}
          />
        </div>
      </div>
      <div className="settingsPageContainer-section">
        <SchemaFieldSelectorOptionsSettings
          isWritable={isWritable}
          options={options}
          setOptions={setOptions}
          selectedArchivePurposes={selectedArchivePurposes}
          handleChangeSelectedArchivePurposes={handleChangeSelectedArchivePurposes}
          selectorId={selectorId}
          updateSelector={updateSelector}
        />
      </div>
    </div>
  );
}

export function ExistingSchemaFieldSelectorSettings({
  relatedSchemasAndInterfaces,
  ...props
}: EnhancedProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<ExistingSchemaFieldSelectorTab>(
    ExistingSchemaFieldSelectorTab.DROPDOWN_SETTINGS
  );

  return (
    <TabContainer
      className="u-flex-grow"
      activeTab={activeTab}
      navClassName={css(styles.nav)}
      paneClassName={css(styles.pane)}
      onRequestTabChange={(tabKey: string) => {
        setActiveTab(tabKey as ExistingSchemaFieldSelectorTab);
      }}
    >
      <Tab tabKey={ExistingSchemaFieldSelectorTab.DROPDOWN_SETTINGS} title="DROPDOWN SETTINGS">
        <DropdownSettings {...props} />
      </Tab>
      <Tab
        tabKey={ExistingSchemaFieldSelectorTab.SCHEMAS}
        title={
          BenchlingConfig.SCHEMAS_ENABLE_INTERFACES
            ? `SCHEMAS AND ${schemaFieldsetNamePluralized().toUpperCase()}`
            : 'SCHEMAS'
        }
      >
        {relatedSchemasAndInterfaces == null ? null : (
          <RelatedSchemasTable schemas={relatedSchemasAndInterfaces} />
        )}
      </Tab>
    </TabContainer>
  );
}

export const EXISTING_SCHEMA_FIELD_SELECTOR_QUERY = gql`
  query ExistingSchemaFieldSelectorQuery($selectorId: StrictString!) @engComponent(name: SCHEMAS) @skipAudit {
    schemaFieldSelector(id: $selectorId) {
      ... on SchemaFieldSelectorNode {
        id
        relatedSchemasAndInterfaces {
          ... on SchemaInterface {
            id
            name
            ... on HasArchiveRecordInterface {
              ...HasArchiveRecordGraphQL
            }
          }
          ... on SchemaInterfaceNode {
            id
            name
            archiveRecord {
              ...ArchiveRecordGraphQL
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ArchiveRecord').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

export default function QueryingExistingSchemaFieldSelectorSettings(
  props: SchemaFieldSelectorSettingsComponentProps
): JSX.Element {
  const dropdownSettingsQuery = useBenchlingQuery<ExistingSchemaFieldSelectorQuery>(
    EXISTING_SCHEMA_FIELD_SELECTOR_QUERY,
    {
      variables: {selectorId: props.selectorId},
    }
  );

  if (dropdownSettingsQuery.type === QueryResultType.PENDING) {
    return <ExistingSchemaFieldSelectorSettings {...props} relatedSchemasAndInterfaces={null} />;
  } else if (dropdownSettingsQuery.type === QueryResultType.SUCCESS) {
    const {data} = dropdownSettingsQuery;
    if (isInaccessible(data.schemaFieldSelector)) {
      return (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this dropdown." />
      );
    } else {
      const relatedSchemasAndInterfaces = data.schemaFieldSelector.relatedSchemasAndInterfaces.filter(
        (schemaOrInterface) => !isInaccessible(schemaOrInterface)
      ) as Array<SchemaNode | ArchivableSchemaNode | SchemaInterfaceNode>;
      return (
        <ExistingSchemaFieldSelectorSettings
          {...props}
          relatedSchemasAndInterfaces={relatedSchemasAndInterfaces}
        />
      );
    }
  } else {
    return (
      <InlineBanner bannerType={InlineBannerType.ERROR} text="There was an error loading this dropdown." />
    );
  }
}

const styles = StyleSheet.create({
  pane: {
    marginTop: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    marginLeft: 17,
    marginRight: 0,
  },
});
